import { useState, useEffect, useRef } from "react";
import { callOperation } from "../../../utils/apiHelper.js";

const initialFilters = {
  range: {
    id: "range",
    fields: [
      {
        headerName: "Genomic Location",
        cellDataType: "text",
        value: "",
        field: "region",
      },
    ],
    title: "Genomic Range",
    collapsed: false,
  },
  symbol: {
    id: "symbol",
    fields: [
      {
        headerName: "Selected Gene Symbol",
        cellDataType: "text",
        value: "",
        field: "feature",
      },
    ],
    title: "Gene Symbols",
    collapsed: false,
  },
  attribute: {
    id: "attribute",
    fields: [],
    title: "Attribute Filter",
    collapsed: false,
  },
};

export const useVariantFilters = (
  token,
  refreshCache = () => {},
  setFilterResult = () => {}
) => {
  const [filterData, setFilterData] = useState({});
  const [filters, setFilters] = useState(initialFilters);
  const [filterMessage, setFilterMessage] = useState(null);
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [DiseaseDatasets, setDiseaseDatasets] = useState([
    "knowledge",
    "experiments",
    // "textmining", // Disabled by default - too large (197MB), causes Lambda OOM
  ]);
  const [confidence_score, setConfidenceScore] = useState("4");
  const [selectedPanels, setSelectedPanels] = useState([]);
  const attributeCompleteCallRef = useRef(null);
  const attributeCompleteTimeoutRef = useRef(null);

  // Auto-dismiss filter messages after 3 seconds for success and info messages
  useEffect(() => {
    if (
      filterMessage &&
      (filterMessage.type === "success" || filterMessage.type === "info")
    ) {
      const timer = setTimeout(() => {
        setFilterMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    // Don't auto-dismiss "processing" or "error" messages
  }, [filterMessage]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (attributeCompleteTimeoutRef.current) {
        clearTimeout(attributeCompleteTimeoutRef.current);
        attributeCompleteTimeoutRef.current = null;
      }
    };
  }, []);

  // Function to fetch complete attribute results
  const fetchCompleteAttributeResults = async () => {
    // Prevent multiple simultaneous calls
    if (attributeCompleteCallRef.current) {
      return;
    }

    try {
      attributeCompleteCallRef.current = true;

      // Show processing message
      setFilterMessage({
        type: "processing",
        message: "Fetching complete results...",
      });

      const response = await callOperation(
        "get_complete_attribute_results",
        {},
        token
      );

      if (response.data.error) {
        setFilterMessage({
          type: "error",
          message: response.data.error,
        });
      } else {
        const totalCount = response.data.total_count || 0;
        setFilterMessage({
          type: "success",
          message: `Attribute filter completed: ${totalCount.toLocaleString()} variants found`,
        });

        // Update filter results
        setFilterResult(
          response.data.filter_results,
          response.data.group_counts
        );
        refreshCache();
      }
    } catch (error) {
      console.error("Error fetching complete attribute results:", error);
      setFilterMessage({
        type: "error",
        message: `Failed to get complete results: ${error.message}`,
      });
    } finally {
      attributeCompleteCallRef.current = false;
    }
  };


  const addFilter = async (field, value, title) => {
    if (value === undefined) {
      return;
    }

    // For attribute filters, show initial processing message
    if (field === "attribute_filter") {
      setFilterMessage({
        type: "processing",
        message: `Generating B-tree index for attribute filter...`,
      });
    }

    try {
      const response = await callOperation(
        "add_filter",
        { field, value, title },
        token
      );

      if (response.data.error) {
        setFilterMessage({
          type: "error",
          message: `Invalid filter: ${response.data.error}`,
        });
        return;
      }

      setFilterResult(
        response.data["filter_results"],
        response.data["group_counts"]
      );

      // Show different messages for attribute filters vs others
      if (field === "attribute_filter") {
        // Get the attribute filter from response
        const attributeFilter = response.data.filter_results?.find(
          (f) => f.type === "attribute"
        );

        if (attributeFilter) {
          const initialCount = attributeFilter.individual_rows;

          // Check if results are complete
          if (!attributeFilter.is_complete) {
            // Show initial results and fetch complete set
            setFilterMessage({
              type: "info",
              message: `Found ${(
                initialCount || 0
              ).toLocaleString()} initial variants, fetching complete results...`,
            });

            // Fetch complete results after a short delay
            attributeCompleteTimeoutRef.current = setTimeout(() => {
              fetchCompleteAttributeResults();
            }, 100);
          } else {
            // All results retrieved in first call
            setFilterMessage({
              type: "success",
              message: `Attribute filter completed: ${(
                initialCount || 0
              ).toLocaleString()} variants found`,
            });
          }
        } else {
          // Filter was eliminated (tautology)
          setFilterMessage({
            type: "success",
            message: "Filter was simplified - no filtering needed",
          });
        }
      } else {
        setFilterMessage({
          type: "success",
          message: `Filter "${title}" applied successfully`,
        });
      }

      refreshCache();
    } catch (error) {
      console.error("Error adding filter:", error);
      setFilterMessage({
        type: "error",
        message: `Failed to add filter: ${error.message}`,
      });
    }
  };

  const clearFilters = async () => {
    // Clear any processing message and pending timeout
    if (attributeCompleteTimeoutRef.current) {
      clearTimeout(attributeCompleteTimeoutRef.current);
      attributeCompleteTimeoutRef.current = null;
    }
    setFilterMessage(null);

    try {
      await callOperation("clear_filter", {}, token);
      // Only clear local state after backend operation succeeds
      setFilterResult([], {});
      refreshCache();
    } catch (error) {
      console.error("Error clearing filters:", error);
      setFilterMessage({
        type: "error",
        message: `Failed to clear filters: ${error.message}`,
      });
    }
  };

  const updateFilterLogic = async (logicSettings) => {
    try {
      const response = await callOperation(
        "set_filter_logic",
        logicSettings,
        token
      );

      if (response.data.error) {
        console.error("Error updating filter logic:", response.data.error);
        return;
      }

      setFilterResult(
        response.data["filter_results"],
        response.data["group_counts"]
      );
      refreshCache();
    } catch (error) {
      console.error("Error updating filter logic:", error);
    }
  };

  const deleteFilter = async (filterType, filter_results) => {
    try {
      // Clear any processing message and timeout if deleting an attribute filter
      if (filterType === "attribute") {
        if (attributeCompleteTimeoutRef.current) {
          clearTimeout(attributeCompleteTimeoutRef.current);
          attributeCompleteTimeoutRef.current = null;
        }
        setFilterMessage(null);
      }

      // First call the backend to delete the filter by type
      await callOperation("delete_filter", { filter_id: filterType }, token);

      // Only update the UI if the backend call succeeded
      const temp = filter_results.filter((f) => f.type !== filterType);
      setFilterResult(temp, {}); // Clear group counts - they will be updated by refreshCache
      refreshCache();
    } catch (error) {
      console.error("Error deleting filter:", error);
      setFilterMessage({
        type: "error",
        message: `Failed to delete filter: ${error.message}`,
      });
    }
  };

  return {
    filterData,
    setFilterData,
    filters,
    setFilters,
    filterMessage,
    setFilterMessage,
    selectedDiseases,
    setSelectedDiseases,
    DiseaseDatasets,
    setDiseaseDatasets,
    confidence_score,
    setConfidenceScore,
    selectedPanels,
    setSelectedPanels,
    addFilter,
    clearFilters,
    deleteFilter,
    updateFilterLogic,
  };
};
